import { PropsWithChildren, ReactNode, useState } from 'react'
import Modal from '../../components/common/modal/modal'
import { useTranslation } from 'react-i18next'
import Button, { ButtonType } from '../../components/common/button'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

type ConfirmState = {
    title: string
    description: string
    content?: ReactNode
    textLeftText: string | null
    textRightText: string | null
    textLeftType: ButtonType
    textRightType: ButtonType
    iconLeft?: IconProp
    iconRight?: IconProp
}

const useConfirm = () => {
    const [open, setOpen] = useState(false)
    const [resolver, setResolver] = useState({ resolve: null as any })
    const { t } = useTranslation()
    const [confirmState, setConfirmState] = useState<ConfirmState>({
        title: '',
        description: '',
        textLeftText: t('no'),
        textRightText: t('yes'),
        textLeftType: 'squareYellow',
        textRightType: 'squareBlue',
    })

    const createPromise = () => {
        let resolver
        return [
            new Promise((resolve, _reject) => {
                resolver = resolve
            }),
            resolver,
        ]
    }

    const getConfirmation = async (state: Partial<ConfirmState>): Promise<boolean | undefined> => {
        setConfirmState({ ...confirmState, ...state })
        setOpen(true)
        const [promise, resolve] = await createPromise()
        setResolver({ resolve } as any)

        return promise as any
    }

    const onClick = async (status: true | false | undefined) => {
        setOpen(false)
        resolver.resolve(status)
    }

    const Confirmation: React.FC<PropsWithChildren> = (props) => (
        <Modal open={open} title={confirmState.title ? t(confirmState.title) : ''} onClose={() => onClick(undefined)} dialogClass="ringo-confirm">
            {confirmState.description && <p className="pt-5">{t(confirmState.description)}</p>}
            {confirmState.content}
            {props.children}
            <div className="pt-8 gap-3 flex justify-center">
                {confirmState.textLeftText && (
                    <Button
                        className="confirm-left"
                        icon={confirmState.iconLeft}
                        type={confirmState.textLeftType}
                        text={confirmState.textLeftText}
                        onClick={() => onClick(false)}
                    />
                )}
                {confirmState.textRightText && (
                    <Button
                        className="confirm-right"
                        icon={confirmState.iconRight}
                        type={confirmState.textRightType}
                        text={confirmState.textRightText}
                        onClick={() => onClick(true)}
                    />
                )}
            </div>
        </Modal>
    )

    return [getConfirmation, Confirmation]
}

export type ConfirmMethod = (config: Partial<ConfirmState>) => Promise<boolean | undefined>

export default useConfirm as () => [ConfirmMethod, React.FC]
